import { useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabase';
import type { EngineTypeKey, MachineSpecificationValue } from '../../planning/types';
import { attachMachineTemplateOptionsAndSpecifications, MACHINE_TEMPLATE_SELECT } from '../../planning/hooks/machineTemplateQueries';
import type { SettingsMachineTemplateRow } from '../types';

interface UseManageMachineTemplatesReturn {
  templates: SettingsMachineTemplateRow[];
  archivedTemplates: SettingsMachineTemplateRow[];
  archivedLoaded: boolean;
  loadArchived: () => Promise<void>;
  addMachineTemplate: (
    name: string,
    categoryId: string | null,
    engineType: EngineTypeKey | null,
    supplierId: string | null,
    optionIds: string[],
    specifications: MachineSpecificationValue[],
  ) => Promise<boolean>;
  updateMachineTemplate: (
    id: string,
    name: string,
    categoryId: string | null,
    engineType: EngineTypeKey | null,
    supplierId: string | null,
    optionIds: string[],
    specifications: MachineSpecificationValue[],
  ) => Promise<boolean>;
  archiveMachineTemplate: (id: string) => Promise<boolean>;
  unarchiveMachineTemplate: (id: string) => Promise<boolean>;
  deleteMachineTemplate: (id: string) => Promise<boolean>;
}

export function useManageMachineTemplates(orgId: string | null): UseManageMachineTemplatesReturn {
  const [templates, setTemplates] = useState<SettingsMachineTemplateRow[]>([]);
  const [archivedTemplates, setArchivedTemplates] = useState<SettingsMachineTemplateRow[]>([]);
  const [archivedLoaded, setArchivedLoaded] = useState(false);

  const loadActive = async () => {
    if (!orgId) {
      setTemplates([]);
      return;
    }
    try {
      const { data } = await supabase
        .from('machine_templates')
        .select(MACHINE_TEMPLATE_SELECT)
        .eq('organization_id', orgId)
        .eq('archived', false)
        .order('name');
      setTemplates(await attachMachineTemplateOptionsAndSpecifications((data ?? []) as unknown as SettingsMachineTemplateRow[]));
    } catch {
      setTemplates([]);
    }
  };

  const refreshArchived = async (): Promise<boolean> => {
    if (!orgId) return false;
    try {
      const { data } = await supabase
        .from('machine_templates')
        .select(MACHINE_TEMPLATE_SELECT)
        .eq('organization_id', orgId)
        .eq('archived', true)
        .order('name');
      setArchivedTemplates(await attachMachineTemplateOptionsAndSpecifications((data ?? []) as unknown as SettingsMachineTemplateRow[]));
      return true;
    } catch {
      setArchivedTemplates([]);
      return false;
    }
  };

  const loadArchived = async () => {
    if (!orgId || archivedLoaded) return;
    setArchivedLoaded(true);
    const ok = await refreshArchived();
    if (!ok) setArchivedLoaded(false);
  };

  useEffect(() => {
    setArchivedLoaded(false);
    setArchivedTemplates([]);
    loadActive();
  }, [orgId]);

  const addMachineTemplate = async (
    name: string,
    categoryId: string | null,
    engineType: EngineTypeKey | null,
    supplierId: string | null,
    optionIds: string[],
    specifications: MachineSpecificationValue[],
  ): Promise<boolean> => {
    if (!orgId) return false;
    try {
      const { data, error } = await supabase
        .from('machine_templates')
        .insert({
          name,
          category_id: categoryId,
          engine_type: engineType,
          supplier_id: supplierId,
          organization_id: orgId,
          archived: false,
        })
        .select('id')
        .single();
      if (error) throw error;

      const templateId = (data as { id: string } | null)?.id;
      if (templateId && optionIds.length) {
        const { error: optionError } = await supabase.from('machine_template_options').insert(
          optionIds.map((optionId) => ({
            template_id: templateId,
            option_id: optionId,
            organization_id: orgId,
          })),
        );
        if (optionError) throw optionError;
      }
      if (templateId && specifications.length) {
        const { error: specError } = await supabase.from('machine_template_specifications').insert(
          specifications.map((s) => ({
            template_id: templateId,
            specification_key: s.key,
            value: s.value,
            organization_id: orgId,
          })),
        );
        if (specError) throw specError;
      }

      await loadActive();
      return true;
    } catch {
      return false;
    }
  };

  const updateMachineTemplate = async (
    id: string,
    name: string,
    categoryId: string | null,
    engineType: EngineTypeKey | null,
    supplierId: string | null,
    optionIds: string[],
    specifications: MachineSpecificationValue[],
  ): Promise<boolean> => {
    if (!orgId) return false;
    try {
      const { error } = await supabase
        .from('machine_templates')
        .update({ name, category_id: categoryId, engine_type: engineType, supplier_id: supplierId })
        .eq('id', id);
      if (error) throw error;

      const { error: deleteError } = await supabase
        .from('machine_template_options')
        .delete()
        .eq('template_id', id);
      if (deleteError) throw deleteError;

      if (optionIds.length) {
        const { error: insertError } = await supabase.from('machine_template_options').insert(
          optionIds.map((optionId) => ({
            template_id: id,
            option_id: optionId,
            organization_id: orgId,
          })),
        );
        if (insertError) throw insertError;
      }

      // Specifications are always submitted as the full set from the modal (max 12 rows), so
      // delete+reinsert is simpler than diffing — same reasoning as category_specifications in
      // useManageCategories, and matches the delete+reinsert already used for options above.
      const { error: deleteSpecError } = await supabase
        .from('machine_template_specifications')
        .delete()
        .eq('template_id', id);
      if (deleteSpecError) throw deleteSpecError;

      if (specifications.length) {
        const { error: insertSpecError } = await supabase.from('machine_template_specifications').insert(
          specifications.map((s) => ({
            template_id: id,
            specification_key: s.key,
            value: s.value,
            organization_id: orgId,
          })),
        );
        if (insertSpecError) throw insertSpecError;
      }

      await loadActive();
      if (archivedLoaded) await refreshArchived();
      return true;
    } catch {
      return false;
    }
  };

  const archiveMachineTemplate = async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase.from('machine_templates').update({ archived: true }).eq('id', id);
      if (error) throw error;
      setTemplates((prev) => prev.filter((t) => t.id !== id));
      if (archivedLoaded) await refreshArchived();
      return true;
    } catch {
      return false;
    }
  };

  const unarchiveMachineTemplate = async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase.from('machine_templates').update({ archived: false }).eq('id', id);
      if (error) throw error;
      setArchivedTemplates((prev) => prev.filter((t) => t.id !== id));
      await loadActive();
      return true;
    } catch {
      return false;
    }
  };

  // Templates are never referenced by machines (a machine only copies template values
  // at creation time), so there is no has_machines-style delete guard here.
  const deleteMachineTemplate = async (id: string): Promise<boolean> => {
    try {
      const { error: optionError } = await supabase
        .from('machine_template_options')
        .delete()
        .eq('template_id', id);
      if (optionError) throw optionError;

      const { error: specError } = await supabase
        .from('machine_template_specifications')
        .delete()
        .eq('template_id', id);
      if (specError) throw specError;

      const { error } = await supabase.from('machine_templates').delete().eq('id', id);
      if (error) throw error;

      setTemplates((prev) => prev.filter((t) => t.id !== id));
      setArchivedTemplates((prev) => prev.filter((t) => t.id !== id));
      return true;
    } catch {
      return false;
    }
  };

  return {
    templates,
    archivedTemplates,
    archivedLoaded,
    loadArchived,
    addMachineTemplate,
    updateMachineTemplate,
    archiveMachineTemplate,
    unarchiveMachineTemplate,
    deleteMachineTemplate,
  };
}
